/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ValuationApproachDCF } from 'api';
import { VALUATIONS_OTHER_LABEL } from 'common/constants/valuations';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { APPROACH_SELECTIONS_ALIASES, TERMINAL_VALUE, TERMINAL_VALUE_OPTIONS } from 'pages/Valuations/util/constants';
import DCFApproachContext from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/context/DCFApproachContext';
import {
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { ValuationSpreadsheets } from 'pages/ValuationsAllocation/hooks/useCreateValuation/types';
import { getSelectionCellOptions } from 'pages/ValuationsAllocation/util';
import { getApproachesByType } from 'pages/ValuationsAllocation/util/getApproachesByType';
import { getObjectValue, getStringValue } from 'utilities';
import DCFTVTContext from './context/DCFTVTContext';
import { DCFTerminalValueTableProps, GetApproachSelectedParams } from './types';

const tvtSpreadSheetKey = {
  [TERMINAL_VALUE_OPTIONS.PERPETUITY_GROWTH]: 'dcfTVTPG',
  [TERMINAL_VALUE_OPTIONS.H_MODEL]: 'dcfTVTHM',
  [TERMINAL_VALUE_OPTIONS.REVENUE_MULTIPLE]: 'dcfTVTRM',
  [TERMINAL_VALUE_OPTIONS.EBITDA_MULTIPLE]: 'dcfTVTEM',
  [TERMINAL_VALUE_OPTIONS.REVENUE_AND_EBITDA_MULTIPLE]: 'dcfTVTREM',
};

const getApproachSelected = (params: GetApproachSelectedParams) => {
  const { approaches, selectedApproachId } = params;
  const filteredApproaches = getApproachesByType({
    approaches,
    approachTypes: [VALUATIONS_PUBLIC_COMPANIES_APPROACH, VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH],
  });
  const idToMatch = selectedApproachId?.toString();
  return filteredApproaches.find(
    ({ panelId, valuations_approach_gpc, valuations_approach_gpt }) =>
      panelId === idToMatch
      || valuations_approach_gpc?.id?.toString() === idToMatch
      || valuations_approach_gpt?.id?.toString() === idToMatch
  );
};

const DCFTerminalValueTable = (props: DCFTerminalValueTableProps) => {
  const {
    format,
    formatDispatch,
    spreadsheets,
    onChange: onWorkbookChange,
    workbook,
    terminalValue,
    approach,
    approaches,
  } = props;
  const initialDcfTvtProps = tvtSpreadSheetKey[terminalValue ?? TERMINAL_VALUE_OPTIONS.PERPETUITY_GROWTH];
  const [dcfTVTProps, setDcfTVTProps] = useState(spreadsheets[initialDcfTvtProps as ValuationSpreadsheets]);

  const { valuations_approach_dcf } = getObjectValue(approach);
  const {
    revenue_gpc_approach_selection,
    revenue_gpt_approach_selection,
    ebitda_gpc_approach_selection,
    ebitda_gpt_approach_selection,
  } = getObjectValue(valuations_approach_dcf);

  const revenueApproachSelected = revenue_gpc_approach_selection ?? revenue_gpt_approach_selection;
  const ebitdaApproachSelected = ebitda_gpc_approach_selection ?? ebitda_gpt_approach_selection;

  const [revenueSelectedApproach, setRevenueSelectedApproach] = useState(
    getApproachSelected({ approaches, selectedApproachId: revenueApproachSelected })
  );
  const [ebitdaSelectedApproach, setEbitdaSelectedApproach] = useState(
    getApproachSelected({ approaches, selectedApproachId: ebitdaApproachSelected })
  );
  const { setCurrentTerminalValue, currentTerminalValue } = useContext(DCFApproachContext);

  const changeTvtType = useCallback(
    ({ tvtType }) => {
      const newSpreadsheetConfigs = spreadsheets[tvtSpreadSheetKey[tvtType] as ValuationSpreadsheets];
      const spreadsheetsKeys = Object.values(tvtSpreadSheetKey) as ValuationSpreadsheets[];
      spreadsheetsKeys.forEach(key => {
        const spreadsheetsColumns = (spreadsheets[key].columns as ValuationApproachDCF[])[0];
        spreadsheetsColumns.terminal_value = tvtType;
      });
      setCurrentTerminalValue(newSpreadsheetConfigs);
    },
    [setCurrentTerminalValue, spreadsheets]
  );

  const getMultipleOptions = useCallback(selectedApproach => {
    const approach = selectedApproach.valuations_approach_gpc ?? selectedApproach.valuations_approach_gpt;
    return [...getSelectionCellOptions({ specificApproach: approach }), VALUATIONS_OTHER_LABEL];
  }, []);

  const revenueMultipleOptions = useMemo(() => {
    if (revenueSelectedApproach) {
      return getMultipleOptions(revenueSelectedApproach);
    }
    return [VALUATIONS_OTHER_LABEL];
  }, [revenueSelectedApproach, getMultipleOptions]);

  const ebitdaMultipleOptions = useMemo(() => {
    if (ebitdaSelectedApproach) {
      return getMultipleOptions(ebitdaSelectedApproach);
    }
    return [VALUATIONS_OTHER_LABEL];
  }, [ebitdaSelectedApproach, getMultipleOptions]);

  useEffect(() => {
    if (currentTerminalValue) {
      setDcfTVTProps(currentTerminalValue);
    }
  }, [currentTerminalValue, revenueMultipleOptions, ebitdaMultipleOptions]);

  const handleApproachSelection = useCallback(
    cell => {
      const foundApproach = getApproachSelected({ approaches, selectedApproachId: cell.value });
      const terminalValue = valuations_approach_dcf?.terminal_value ?? '';
      if (terminalValue === TERMINAL_VALUE_OPTIONS.REVENUE_MULTIPLE) {
        setRevenueSelectedApproach(foundApproach);
      } else if (terminalValue === TERMINAL_VALUE_OPTIONS.EBITDA_MULTIPLE) {
        setEbitdaSelectedApproach(foundApproach);
      } else if (terminalValue === TERMINAL_VALUE_OPTIONS.REVENUE_AND_EBITDA_MULTIPLE) {
        if (cell.columnLegend === 'A') {
          setRevenueSelectedApproach(foundApproach);
        } else {
          setEbitdaSelectedApproach(foundApproach);
        }
      }
    },
    [approaches, valuations_approach_dcf]
  );

  const onChange = useCallback(
    (cell, expr) => {
      if (onWorkbookChange) {
        onWorkbookChange(cell, expr);
      }

      if (cell.alias === TERMINAL_VALUE) {
        changeTvtType({ tvtType: expr });
      }
      if (APPROACH_SELECTIONS_ALIASES.includes(cell.alias)) {
        handleApproachSelection(cell);
      }
    },
    [changeTvtType, onWorkbookChange, handleApproachSelection]
  );

  const contextValue = useMemo(
    () => ({
      revenueMultipleOptions,
      ebitdaMultipleOptions,
    }),
    [revenueMultipleOptions, ebitdaMultipleOptions]
  );

  const dCFTerminalValueTableSheetProps = useMemo(
    () =>
      ({
        ...dcfTVTProps,
        sheet: dcfTVTProps,
        key: getStringValue(dcfTVTProps?.name),
        workbook,
        onChange,
        format,
        formatDispatch,
      } as unknown as typeof ScalarSpreadsheet),
    [dcfTVTProps, workbook, onChange, format, formatDispatch]
  );

  return (
    <div>
      <DCFTVTContext.Provider value={contextValue}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: ScalarSpreadsheet is not typed */}
        <ScalarSpreadsheet {...dCFTerminalValueTableSheetProps} />
      </DCFTVTContext.Provider>
    </div>
  );
};

export default DCFTerminalValueTable;
